import { ExperienceMapper, type ExperienceConfiguration, Reference } from '@ccpgames/eve-web-contentful'
import type { NtExperience } from '@/graphql/types'

type WithSysId = { sys?: { id?: string | null } | null }

type ExperienceVariant = NonNullable<NonNullable<NtExperience['ntVariantsCollection']>['items']>[number]

type ExperienceItem = NonNullable<NonNullable<NtExperience> & WithSysId> & {
  ntVariantsCollection?: { items?: ExperienceVariant[] | null } | null
}

type ExperiencesCollection = {
  sys?: { id?: string | null } | null
  ntExperiencesCollection?: {
    items?: (unknown | null)[] | null
  } | null
}

export type ExperiencesHost<T extends ExperiencesCollection = ExperiencesCollection> = Pick<
  T,
  'sys' | 'ntExperiencesCollection'
>

const toRef = <T extends WithSysId>(x: T | null | undefined) => {
  if (!x?.sys?.id) return null
  const ref = { id: x.sys.id, ...(x as object) } as const
  return ref
}

export const parseExperiences = <TEntry extends ExperiencesCollection>(
  entry: ExperiencesHost<TEntry> | undefined,
): ExperienceConfiguration<TEntry & Reference>[] => {
  const items: ExperienceItem[] = (entry?.ntExperiencesCollection?.items?.filter(Boolean) as ExperienceItem[]) ?? []

  const raw = items.map((exp) => {
    const variants = (exp.ntVariantsCollection?.items ?? [])
      .map((v) => {
        return toRef(v as WithSysId)
      })
      .filter((x): x is { id: string } & object => !!x)

    return {
      id: exp.ntExperienceId || exp.sys?.id,
      name: exp.ntName ?? '',
      type: exp.ntType ?? '',
      config:
        exp.ntConfig && typeof exp.ntConfig === 'object'
          ? (exp.ntConfig as { distribution?: number[]; traffic?: number; components?: unknown[]; sticky?: boolean })
          : undefined,
      audience: exp.ntAudience?.ntAudienceId ? { id: exp.ntAudience.ntAudienceId } : undefined,
      variants,
    }
  })

  const mapped = raw
    .filter((x) => ExperienceMapper.isExperienceEntry(x))
    .map((x) => ExperienceMapper.mapExperience(x))
    .map((e) => {
      const result = {
        ...e,
        components: e.components.map((c) => {
          const type = (c as { type?: string }).type
          if (type === 'EntryReplacement') {
            const component = entry ? { ...c, baseline: toRef(entry as WithSysId) } : c
            return component
          }
          return c
        }),
      }
      return result
    }) as unknown as ExperienceConfiguration<TEntry & Reference>[]

  return mapped
}
